import { useTranslation } from "react-i18next"
import * as SelectPrimitive from "@radix-ui/react-select"
import { ChevronDown } from "lucide-react"
import type { Ref } from "react"

import Icon from "@/components/Icon"
import {
  Select,
  SelectContent,
  SelectGroup,
  selectItemVariants,
  selectTriggerVariants,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/helpers/classNames"

type AgentStatus = "offline" | "online"

type Props = {
  isDisabled: boolean
  onChange: (status: AgentStatus) => void
  status: "loading" | AgentStatus
}

export function AgentStatusSwitcher({ isDisabled, onChange, status }: Props) {
  const { t } = useTranslation()
  const isLoading = status === "loading"

  return (
    <Select
      value={isLoading ? "" : status}
      onValueChange={onChange}
      disabled={isLoading || isDisabled}
    >
      <SelectTrigger
        className={cn("h-auto w-[190px] py-2 pr-2 pl-4 font-button-large")}
      >
        <SelectValue
          placeholder={t("header.agentSelectOptions.loading")}
          unmask
        />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup className="flex flex-col gap-2 p-3">
          <StatusOption status="online" />
          <StatusOption status="offline" />
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

function StatusOption({ status }: { status: AgentStatus }) {
  const { t } = useTranslation()

  return (
    <SelectItem value={status}>
      <div className="flex items-center gap-2">
        <AgentStatusPill status={status} />
        <span data-unmask>{t(`header.agentSelectOptions.${status}`)}</span>
      </div>
    </SelectItem>
  )
}

function AgentStatusPill({ status }: { status: AgentStatus }) {
  switch (status) {
    case "online":
      return <Icon size="sm" name="available" />
    case "offline":
      return <Icon size="sm" name="offline" />
    default:
      return null
  }
}

function SelectTrigger({
  children,
  className,
  ref,
  ...props
}: {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Trigger>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(selectTriggerVariants({ size: "xl" }), className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-5" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
}

function SelectItem({
  children,
  className,
  ref,
  ...props
}: {
  ref?: Ref<React.ElementRef<typeof SelectPrimitive.Item>>
} & React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(selectItemVariants({ size: "xl" }), "pl-4")}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}
